import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowUpRight, BookOpen, Feather, Compass } from "lucide-react";
import BonusCapture from "@/components/BonusCapture";

export default function Home() {
  const [books, setBooks] = useState([]);
  useEffect(() => {
    api.get("/books").then((r) => setBooks(r.data.books || []));
  }, []);

  const featured = books[0];
  const rest = books.slice(1);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 fade-up">
            <p className="eyebrow" data-testid="hero-eyebrow">A quiet publisher · Est. 2026</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[104px] leading-[0.95] tracking-tighter mt-6" data-testid="hero-title">
              Books for<br/>
              <em className="italic text-[#B85C38]">a considered</em><br/>
              life.
            </h1>
            <p className="mt-8 text-[17px] leading-relaxed text-[#5C5C5C] max-w-xl">
              Slow, careful writing about attention, habit, and the interior work of becoming — for readers who prefer depth to slogans.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              {featured && (
                <Link to={`/checkout/${featured.id}`} data-testid="hero-cta-buy" className="btn-primary">
                  Buy the book · ${featured.price?.toFixed(2)} <ArrowUpRight size={16} />
                </Link>
              )}
              <Link to="/catalog" data-testid="hero-cta-catalog" className="btn-ghost">
                Browse Catalog
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up fade-up-delay-2">
            <div className="relative">
              <div className="aspect-[4/5] w-full overflow-hidden bg-[#E5E2DC]">
                <img
                  src={featured?.promo_image || "https://customer-assets.emergentagent.com/job_digital-library-217/artifacts/o7jseukk_1000018263.png"}
                  alt={featured?.title || "The Architecture of Discipline"}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden sm:block bg-[#F9F8F6] border border-[#E5E2DC] p-5 max-w-[240px]">
                <p className="eyebrow">Volume I</p>
                <p className="font-serif-display text-xl leading-tight mt-2">{featured?.title || "The Architecture of Discipline"}</p>
                <p className="text-xs text-[#5C5C5C] mt-2">{featured?.pages || 62} pp{featured?.bonus_pages ? ` + ${featured.bonus_pages} pp bonus` : ""} · {featured?.reading_time_min || 90} min read</p>
              </div>
            </div>
          </div>
        </div>
        <div className="hairline max-w-[1360px] mx-auto" />
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1360px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <p className="eyebrow">Our Position</p>
        </div>
        <div className="lg:col-span-8">
          <p className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
            We publish few books, slowly. Each one is meant to be lived with, not skimmed — a small argument, made carefully, that a truer life is possible without another hack, another app, another louder version of yourself.
          </p>
        </div>
      </section>

      <div className="hairline max-w-[1360px] mx-auto" />

      {/* FEATURED BOOK */}
      {featured && (
        <section id="featured" className="max-w-[1360px] mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <p className="eyebrow">The Featured Volume</p>
              <h2 className="font-serif-display text-5xl sm:text-6xl leading-none tracking-tight mt-4" data-testid="featured-title">
                {featured.title}
              </h2>
              <p className="font-serif-display italic text-2xl text-[#5C5C5C] mt-3">{featured.subtitle}</p>
              <p className="mt-8 text-[16px] leading-relaxed text-[#1C1C1C] max-w-xl">
                {featured.short_description}
              </p>
              <div className="mt-8 flex flex-wrap gap-3 items-baseline">
                <Link to={`/checkout/${featured.id}`} data-testid="featured-buy-cta" className="btn-primary">
                  Get your copy · ${featured.price?.toFixed(2)}
                </Link>
                <Link to={`/book/${featured.slug}`} data-testid="featured-cta" className="btn-ghost">
                  Read a chapter
                </Link>
              </div>
              <div className="mt-4">
                <span className="eyebrow">Instant PDF delivery · Free bonus workbook</span>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-md border-t border-[#E5E2DC] pt-6">
                <div>
                  <p className="font-serif-display text-3xl">{featured.pages}<span className="text-lg text-[#5C5C5C]">{featured.bonus_pages ? `+${featured.bonus_pages}` : ""}</span></p>
                  <p className="eyebrow mt-1">Pages</p>
                </div>
                <div>
                  <p className="font-serif-display text-3xl">{Math.round(featured.reading_time_min/60)}h</p>
                  <p className="eyebrow mt-1">Reading</p>
                </div>
                <div>
                  <p className="font-serif-display text-3xl">{featured.chapters?.length || 12}</p>
                  <p className="eyebrow mt-1">Chapters</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-[3/4] max-h-[720px] overflow-hidden bg-[#E5E2DC]">
                  <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="hairline max-w-[1360px] mx-auto" />

      {/* WHAT WE VALUE */}
      <section className="max-w-[1360px] mx-auto px-6 lg:px-12 py-24">
        <p className="eyebrow">What our books value</p>
        <div className="grid md:grid-cols-3 gap-10 mt-10">
          {[
            { icon: <Feather size={22} />, title: "Slow prose", body: "Sentences worth re-reading. No filler. No thought-leader voice." },
            { icon: <Compass size={22} />, title: "Grown-up practice", body: "Practices you can actually keep on a Tuesday, not a retreat." },
            { icon: <BookOpen size={22} />, title: "A whole book", body: "Long-form thinking. Not a listicle stretched to 200 pages." },
          ].map((v, i) => (
            <div key={i} className="border-t border-[#E5E2DC] pt-6" data-testid={`value-card-${i}`}>
              <div className="text-[#B85C38]">{v.icon}</div>
              <h3 className="font-serif-display text-2xl mt-4">{v.title}</h3>
              <p className="text-[#5C5C5C] mt-3 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FREE BONUS WORKBOOK */}
      <section className="max-w-[1360px] mx-auto px-6 lg:px-12 pb-24">
        <BonusCapture variant="block" />
      </section>

      {/* CATALOG PREVIEW */}
      {rest.length > 0 && (
        <section className="max-w-[1360px] mx-auto px-6 lg:px-12 py-24 border-t border-[#E5E2DC]">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow">Catalog</p>
              <h2 className="font-serif-display text-4xl sm:text-5xl mt-3 leading-none">Also on the shelf</h2>
            </div>
            <Link to="/catalog" className="link-underline text-sm hidden sm:inline" data-testid="see-all-books">See all →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {rest.map((b) => (
              <Link key={b.id} to={`/book/${b.slug}`} data-testid={`catalog-book-${b.slug}`} className="group">
                <div className="aspect-[3/4] overflow-hidden bg-[#E5E2DC]">
                  <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <p className="eyebrow mt-4">{b.categories?.[0] || "Volume"}</p>
                <h3 className="font-serif-display text-2xl mt-2 leading-tight">{b.title}</h3>
                <p className="text-[#5C5C5C] mt-1 italic font-serif-display">{b.subtitle}</p>
                <p className="mt-3 text-sm">${b.price?.toFixed(2)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
